import { useState, useMemo, useEffect, useRef } from "react";
import {
  ConversationState,
  ConversationStep,
  getCurrentStep,
  getAnswers,
} from "../../packages/conversation-core/src";
import {
  intakeConversationSteps,
  intakeConversationEngine,
} from "../conversation/intakeConversation";
import { ChatThreadMessage, ExpertPersona, AIMessage } from "../components/chat/types";
import { msgId, nowIso } from "../utils/chat";

/**
 * Dynamic Expert Routing for the 45-member panel.
 */
function getIntakeExpert(step: ConversationStep): ExpertPersona {
  const { module, id } = step;

  if (module === "Origin") {
    if (id === "signalUrl") return "Systems Architect";
    return "Intake Scout";
  }

  if (module === "You") {
    if (id === "skills") return "Skills Gap Analyst";
    return "Resume Architect";
  }

  if (module === "Alignment") {
    if (id === "targetRole") return "Chief Strategy Officer";
    return "Alignment Mapper";
  }

  return "Monyawn Wingman";
}

export function useChatIntake(
  options: {
    isIntakeMode?: boolean;
    onIntakeComplete?: (answers: Record<string, any>) => void;
  },
  messages: ChatThreadMessage[],
  setMessages: React.Dispatch<React.SetStateAction<ChatThreadMessage[]>>,
  pushSystemMessage: (text: string) => void,
) {
  const [intakeState, setIntakeState] = useState<ConversationState>(() =>
    intakeConversationEngine.createConversationState(),
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const lastProcessedStepId = useRef<string | null>(null);

  const currentStep = useMemo(
    () => getCurrentStep(intakeConversationSteps, intakeState),
    [intakeState],
  );

  useEffect(() => {
    if (!options.isIntakeMode || !currentStep || isProcessing) return;
    if (lastProcessedStepId.current === currentStep.id) return;

    lastProcessedStepId.current = currentStep.id;

    const lastStepId =
      messages.length > 0 ? (messages[messages.length - 1] as any).controlStep?.id : null;
    const lastStep = intakeConversationSteps.find((s) => s.id === lastStepId);

    if (lastStep && lastStep.module !== currentStep.module) {
      pushExpertReview(lastStep.module, currentStep);
    } else {
      pushPrompt(currentStep);
    }
  }, [currentStep?.id, options.isIntakeMode, isProcessing, messages]);

  function pushPrompt(step: ConversationStep) {
    const expert = getIntakeExpert(step);

    const promptMsg: AIMessage = {
      type: "ai",
      id: msgId("prompt"),
      expert,
      text: `${step.segue}\n\n${step.prompt}`,
      timestamp: nowIso(),
      controlStep: step,
      controlValue: "",
    };

    setMessages((prev) => [...prev, promptMsg]);
  }

  async function pushExpertReview(module: string, nextStep: ConversationStep) {
    setIsProcessing(true);

    // Select expert for the review phase
    const expert: ExpertPersona =
      module === "Origin"
        ? "Systems Architect"
        : module === "You"
          ? "Chief Strategy Officer"
          : "Chief Strategy Officer";

    const reviewMsg: AIMessage = {
      type: "ai",
      id: msgId("review"),
      expert,
      text: `Hold on, the ${expert} is doing a quick pass on the ${module} details...`,
      timestamp: nowIso(),
      expertGated: true,
      expertReviewStatus: "pending",
    };

    setMessages((prev) => [...prev, reviewMsg]);
    await new Promise((r) => setTimeout(r, 1500));

    setMessages((prev) =>
      prev.map((m) =>
        m.id === reviewMsg.id
          ? {
              ...m,
              expertReviewStatus: "approved",
              text: `${module} details approved. Let's move to the next phase.`,
            }
          : m,
      ),
    );

    await new Promise((r) => setTimeout(r, 800));
    setIsProcessing(false);
    pushPrompt(nextStep);
  }

  function handleControlSubmit(messageId: string, overrideValue?: unknown) {
    const msg = messages.find((m) => m.id === messageId);
    if (msg?.type !== "ai" || !msg.controlStep) return;

    const value = overrideValue !== undefined ? overrideValue : msg.controlValue;
    const error = intakeConversationEngine.validateStep(msg.controlStep, value, intakeState);

    if (error) {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, controlError: error } : m)),
      );
      return;
    }

    const nextState = intakeConversationEngine.applyResponse(
      intakeConversationSteps,
      intakeState,
      msg.controlStep.id,
      value,
    );

    setIntakeState(nextState);
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, controlStep: undefined } : m)),
    );

    if (msg.controlStep.id === "lock_play") {
      if (value === "lock") options.onIntakeComplete?.(getAnswers(nextState));
      else {
        pushSystemMessage("Edit mode selected. Backtracking to the start.");
        setIntakeState(intakeConversationEngine.createConversationState());
        setMessages([]);
        lastProcessedStepId.current = null;
      }
      return;
    }

    const summary = msg.controlStep.summarize?.(value, getAnswers(nextState)) ?? String(value);
    setMessages((prev) => [
      ...prev,
      {
        type: "user",
        id: msgId("user"),
        text: summary,
        timestamp: nowIso(),
      },
    ]);

    const finalStep = getCurrentStep(intakeConversationSteps, nextState);
    if (!finalStep) pushCTOReview();
  }

  async function pushCTOReview() {
    setIsProcessing(true);
    const reviewMsg: AIMessage = {
      type: "ai",
      id: msgId("cto_review"),
      expert: "CTO / Principal",
      text: "The CTO is performing a final technical sweep of the play's architecture and governance...",
      timestamp: nowIso(),
      expertGated: true,
      expertReviewStatus: "pending",
    };

    setMessages((prev) => [...prev, reviewMsg]);
    await new Promise((r) => setTimeout(r, 2000));

    setMessages((prev) =>
      prev.map((m) =>
        m.id === reviewMsg.id
          ? {
              ...m,
              expertReviewStatus: "approved",
              text: "Technical sweep complete. Architecture is sound and governance is locked.",
            }
          : m,
      ),
    );

    await new Promise((r) => setTimeout(r, 1000));
    setIsProcessing(false);
    pushCompletionSummary();
  }

  async function pushCompletionSummary() {
    setIsProcessing(true);
    setMessages((prev) => [
      ...prev,
      {
        type: "ai",
        id: msgId("completion"),
        expert: "Offer Strategist",
        text: "Everything looks solid. Ready to lock this play and jump into the cockpit?",
        timestamp: nowIso(),
        controlStep: {
          id: "lock_play",
          module: "Finish",
          label: "Lock it in?",
          segue: "",
          prompt: "",
          control: "choice",
          options: [
            { value: "lock", label: "Lock the play 🚀" },
            { value: "edit", label: "Wait, let me edit something" },
          ],
        },
        controlValue: "",
      },
    ]);
    setIsProcessing(false);
  }

  function handleBacktrack() {
    if (intakeState.history.length === 0) {
      pushSystemMessage("You're already at the start.");
      return;
    }

    const lastHistory = intakeState.history[intakeState.history.length - 1];
    if (lastHistory.kind !== "answer") return;

    const stepToReopen = lastHistory.stepId;
    const nextResponses = { ...intakeState.responses };
    delete nextResponses[stepToReopen];

    setIntakeState((prev) => ({
      ...prev,
      responses: nextResponses,
      history: prev.history.slice(0, -1),
      staleStepIds: prev.staleStepIds.filter((id) => id !== stepToReopen),
    }));

    setMessages((prev) => {
      const next = [...prev];
      if (next[next.length - 1].type === "user") next.pop();
      if (next[next.length - 1].type === "ai") next.pop();
      return next;
    });

    lastProcessedStepId.current = null;
    pushSystemMessage(`Undid ${stepToReopen}.`);
  }

  return {
    intakeState,
    isProcessing,
    currentStep,
    handleControlSubmit,
    handleBacktrack,
  };
}
